import { Provider, inject } from '@angular/core';
import { environment } from '@ups/xplat/core';
import {
  IRynReplayIngestTokenSource,
  IRynReplayRecorderBackend,
  NoopRynReplayRecorderBackend,
  resolveRynReplaySessionLifecycleConfig,
  RynReplayHttpTransport,
  RYN_REPLAY_RECORDER_BACKEND,
  setRynReplayOpaqueSegmentRedaction,
} from '@ups/xplat/base/ryn-replay';
import { RrwebRynReplayRecorderBackend } from './ryn-replay-recorder.rrweb-backend';
import { createRynReplayOfflineStore } from './ryn-replay-offline-store.indexeddb';
import {
  RYN_REPLAY_BEARER_PROVIDER,
  RynReplayBearerProvider,
} from './ryn-replay-bearer-provider';
import {
  RynReplayIngestTokenClient,
  deriveRynReplayIngestTokenUrl,
} from './ryn-replay-ingest-token-client';
import { createRynReplayGzipCompressor } from './ryn-replay-gzip-worker';

/**
 * Turns an app on for real rrweb capture (#30070).
 *
 * The backend token defaults to the no-op implementation, so no app records
 * anything until it adds `provideRynReplayRrwebBackend()` to its providers.
 * Keeping the wiring here means opting an app in is one line instead of twenty
 * lines of transport and batching setup copy-pasted per app.
 *
 * Why this checks more than `enabled`:
 *
 * The recorder service checks `enabled` on its own, and that is right for its
 * job. It decides whether a session exists. This decides whether a session can
 * actually be delivered, which is a different question.
 *
 * Say an app sets `enabled: true` but has no ingest url or key. It would attach
 * rrweb, capture a real user's screen, then find out batch by batch that it has
 * nowhere to send any of it. Nothing leaks, the transport refuses to post to an
 * unconfigured endpoint. But the user's tab pays the full cost of capture and
 * holds up to four dead batches in memory, all for a session nobody can ever
 * read back.
 *
 * So a half-configured app falls back to the no-op backend. No rrweb download,
 * no listeners, no memory. Getting the config wrong costs nothing, instead of
 * costing everything except the part you wanted.
 *
 * `app` is in the same check because the server figures out which app is writing
 * from the ingest key, and rejects any batch whose `app` field disagrees. An
 * empty `app` turns every batch into a 400, which looks like a capture problem
 * but is really a config problem.
 */
export function createRynReplayRecorderBackend(
  bearerProvider: RynReplayBearerProvider | null = null
): IRynReplayRecorderBackend {
  const config = environment.rynReplay;

  if (
    !config?.enabled ||
    !config.ingestUrl ||
    !config.ingestKey ||
    !config.app
  ) {
    return new NoopRynReplayRecorderBackend();
  }
  // Per-app opt-in for opaque path segments, read once for every URL scrub.
  setRynReplayOpaqueSegmentRedaction(config.redactOpaquePathSegments === true);

  const transport = new RynReplayHttpTransport(
    {
      ingestUrl: config.ingestUrl,
      ingestKey: config.ingestKey,
      ingestTokens: createRynReplayIngestTokenSource(
        config.ingestUrl,
        bearerProvider
      ),
    },
    createRynReplayGzipCompressor()
  );

  // Same-origin is always eligible; stamp only UPS API origins from env so a
  // recorder cannot force CORS preflights on third-party hosts.
  const correlationAllowedApiBaseUrls = [
    ...Object.values(environment.urls ?? {}).filter(
      (value): value is string => typeof value === 'string' && value.length > 0
    ),
    ...(typeof config.readApiUrl === 'string' && config.readApiUrl.length > 0
      ? [config.readApiUrl]
      : []),
  ];

  return new RrwebRynReplayRecorderBackend(
    transport,
    {
      app: config.app,
      ingestUrl: config.ingestUrl,
      correlationAllowedApiBaseUrls,
      // Leaving these undefined is fine. The batcher falls back to its defaults
      // and clamps whatever it gets to the server's limits, so a typo in Azure
      // App Configuration cannot ask for a batch size the server would reject.
      limits: {
        maxEvents: config.maxBatchEvents,
        maxBytes: config.maxBatchBytes,
      },
      flushIntervalMs: config.flushIntervalMs,
      // Segmentation defaults (30 min idle / 8 h max) live in the recorder
      // library; apps override via environment.rynReplay or pass 0 to disable.
      sessionLifecycle: resolveRynReplaySessionLifecycleConfig({
        idleTimeoutMs: config.idleTimeoutMs,
        maxSessionDurationMs: config.maxSessionDurationMs,
      }),
    },
    undefined,
    createRynReplayOfflineStore()
  );
}

/**
 * Signed ingest token source (#30115 PR 2), or undefined for key-only mode.
 * Undefined unless the app provided a bearer AND the ingest url ends in
 * `/ingest`, so an app that did not opt in never calls the mint.
 */
export function createRynReplayIngestTokenSource(
  ingestUrl: string | undefined,
  bearerProvider: RynReplayBearerProvider | null
): IRynReplayIngestTokenSource | undefined {
  const mintUrl = deriveRynReplayIngestTokenUrl(ingestUrl);
  if (!bearerProvider || !mintUrl) {
    return undefined;
  }
  return new RynReplayIngestTokenClient({ mintUrl, bearerProvider });
}

/**
 * Drop into an app module's `providers` to turn capture on for that app.
 * Reads `environment.rynReplay`, so the decision still lives in the app's own
 * environment file and not in code. Token mode is a second, optional opt-in:
 * provide `RYN_REPLAY_BEARER_PROVIDER` as well.
 */
export const provideRynReplayRrwebBackend = (): Provider => ({
  provide: RYN_REPLAY_RECORDER_BACKEND,
  useFactory: () =>
    createRynReplayRecorderBackend(
      inject(RYN_REPLAY_BEARER_PROVIDER, { optional: true })
    ),
});
